import { useCallback } from "react";
import useAuthService from "./auth.service";
import { useFetch } from "../hooks/useFetch";
import { API_BASE_URL } from "@/constants/app.constant";
import { APIResponse } from "@/interfaces/common.interface";

/**
 * useConfigService hook provides configuration-related API service functions.
 * Handles fetching terms and conditions, banner messages, and updating acceptance.
 * 
 * @hook
 * @returns {Object} Config service methods
 * @returns {Function} returns.fetchTermsAndConditions - Fetch terms and conditions
 * @returns {Function} returns.updateTermsAndConditionsAcceptance - Update acceptance time
 * @returns {Function} returns.fetchBannerMessage - Fetch banner message
 * 
 * @example
 * ```tsx
 * const { fetchTermsAndConditions, fetchBannerMessage } = useConfigService();
 * 
 * const terms = await fetchTermsAndConditions();
 * const banner = await fetchBannerMessage();
 * ```
 */
const useConfigService = () => {
  const fetchService = useFetch();
  const { getUserAccessToken } = useAuthService();

  /**
   * Fetches terms and conditions from the API.
   * 
   * @returns {Promise<APIResponse<TermsAndConditionsResponse> | undefined>} Terms and conditions response
   * @throws {Error} If token is invalid
   */
  const fetchTermsAndConditions = useCallback(async () => {
    try {
      // Get current user access token
      const token = await getUserAccessToken();
      if (!token) {
        throw new Error("Invalid token");
      }

      return await fetchService.get<APIResponse<TermsAndConditionsResponse>>(
        API_BASE_URL + "/config/terms-and-conditions",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
    } catch (err) {
      console.error("Error fetching terms and conditions:", err);
    }
  }, []);

  /**
   * Updates the acceptance time of terms and conditions for the current user.
   * 
   * @param {string} tcVersion - Terms and conditions version
   * @returns {Promise<APIResponse<any>>} Update response
   * @throws {Error} If token is invalid
   */
  const updateTermsAndConditionsAcceptance = useCallback(
    async (tcVersion: string) => {
      // Get current user access token
      const token = await getUserAccessToken();
      if (!token) {
        throw new Error("Invalid token");
      }

      return fetchService.put<APIResponse<any>>(
        `${API_BASE_URL}/config/terms-and-conditions`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: {
            tc_version: tcVersion,
            last_tc_acceptance: new Date().toISOString(),
          },
        }
      );
    },
    []
  );


  /**
   * Fetches the banner message from the API.
   * 
   * @returns {Promise<APIResponse<BannerResponse> | undefined>} Banner message response
   * @throws {Error} If token is invalid
   */
  const fetchBannerMessage = useCallback(async () => {
    try {
      // Get current user access token
      const token = await getUserAccessToken();
      if (!token) {
        throw new Error("Invalid token");
      }

      return await fetchService.get<APIResponse<BannerResponse>>(
        API_BASE_URL + "/config/banner-message",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
    } catch (err) {
      console.error("Error fetching terms and conditions:", err);
    }
  }, []);


  return {
    fetchTermsAndConditions,
    updateTermsAndConditionsAcceptance,
    fetchBannerMessage
  };
};

export default useConfigService;
